import { useState } from "react";

import { browserLogs } from "merchant-portal-frontend/src/logger";
/**
 * Detect whether localStorage or sessionStorage is supported and available
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
 */
function storageAvailable() {
	const storage = window.localStorage;
	try {
		const x = "__storage_test__";
		storage.setItem(x, x);
		storage.removeItem(x);
		return true;
	} catch (e) {
		return (
			e instanceof DOMException &&
			// everything except Firefox
			(e.code === 22 ||
				// Firefox
				e.code === 1014 ||
				// test name field too, because code might not be present
				// everything except Firefox
				e.name === "QuotaExceededError" ||
				// Firefox
				e.name === "NS_ERROR_DOM_QUOTA_REACHED") &&
			// acknowledge QuotaExceededError only if there's something already stored
			storage.length !== 0
		);
	}
}

const memoryStorageFallback = {};

export function getItem<T = unknown>(key: string, defaultValue?: T): T | null {
	if (storageAvailable()) {
		const ret = window.localStorage.getItem(key);
		return ret ? JSON.parse(ret) : defaultValue || null;
	}

	return memoryStorageFallback[key];
}

export function setItem<T = unknown>(key: string, value: T) {
	if (storageAvailable()) {
		window.localStorage.setItem(key, JSON.stringify(value));
	}

	memoryStorageFallback[key] = value;
}

export function removeItem(key: string) {
	if (storageAvailable()) {
		window.localStorage.removeItem(key);
	}

	delete memoryStorageFallback[key];
}

// inspired by https://usehooks.com/useLocalStorage/
export function useLocalStorage<T = unknown>(
	initialKey: string,
	initialValue: T,
): [T, (value: T) => void, (key: string) => void] {
	const [key, setKey] = useState(initialKey);
	const [storedValue, setStoredValue] = useState(() => {
		try {
			const item = window.localStorage.getItem(key);
			return item ? JSON.parse(item) : initialValue;
		} catch (error) {
			return initialValue;
		}
	});

	const setValue = (value: T) => {
		try {
			const valueToStore = value instanceof Function ? value(storedValue) : value;
			setStoredValue(valueToStore);
			window.localStorage.setItem(key, JSON.stringify(valueToStore));
		} catch (error) {
			browserLogs.logger.error({
				message: "failed to store value in local storage",
				context: { key, error },
			});
		}
	};

	const updateKey = (newKey: string) => {
		try {
			setKey(newKey);
			const item = window.localStorage.getItem(newKey);
			setStoredValue(item ? JSON.parse(item) : initialValue);
		} catch (error) {
			browserLogs.logger.error({
				message: "failed to update local storage key",
				context: { key, newKey, error },
			});
		}
	};
	return [storedValue, setValue, updateKey];
}
